<template>
  <div class="overflow-auto h-full">
    <div
      class="flex flex-wrap sm:flex-nowrap items-center justify-between my-4 lg:my-8 px-2"
    >
      <!-- title -->
      <div class="order-1 flex-grow flex items-center">
        <svg
          viewBox="0 0 40 40"
          class="block w-8 h-8 stroke-current text-navy"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)">
            <path
              d="M10.719,5.2A5.251,5.251,0,0,0,.75,7.5V23.25h3"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M0.75 12.75L3.75 12.75"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M0.75 8.25L9 8.25"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M21.75 15L21.75 23.25 6.75 23.25 6.75 15"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M5.25,16.5l7.939-7.939a1.5,1.5,0,0,1,2.122,0L23.25,16.5"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M14.25,12.375a.375.375,0,0,1,.375.375"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M13.875,12.75a.375.375,0,0,1,.375-.375"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M14.25,13.125a.375.375,0,0,1-.375-.375"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M14.625,12.75a.375.375,0,0,1-.375.375"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M17.25,17.25h-6a1.5,1.5,0,0,0-1.5,1.5v4.5h9v-4.5A1.5,1.5,0,0,0,17.25,17.25Z"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
          </g>
        </svg>

        <span class="ml-2 text-navy font-bold leading-7 text-2xl">
          {{ pageTitle }}
        </span>
      </div>

      <!-- Back to Farmers -->
      <div class="order-2 sm:order-3 sm:mr-3">
        <inertia-link
          class="flex items-center justify-center w-7 h-7 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
          :href="route('app.farmer.index')"
        >
          <svg
            viewBox="0 0 40 40"
            xmlns="http://www.w3.org/2000/svg"
            class="block w-7 h-7 stroke-current text-white"
          >
            <g
              transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)"
            >
              <path
                d="M7.501 11.997L16.501 11.997"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
              ></path>
              <path
                d="M11.251 15.747L7.501 11.997 11.251 8.247"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
              ></path>
            </g>
          </svg>
          <span class="sr-only"> Farmers List </span>
        </inertia-link>
      </div>
    </div>

  <!-- Hide flash bar when editing from view page (toast used instead) -->
  <flash-message v-if="!(isEditing && redirectTo === 'view')" />

    <!-- Progress Steps -->
    <div class="bg-white p-4 border-b mt-8">
      <div class="flex items-center justify-center">
        <div class="flex items-center space-x-4">
          <div class="flex items-center" :class="step >= 1 ? 'text-green-600' : 'text-gray-400'">
            <span class="w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium" 
                  :class="step >= 1 ? 'border-green-600 bg-green-600 text-white' : 'border-gray-300'">1</span>
            <span class="ml-2 text-sm font-medium">Farmer Info</span>
          </div>
          <div class="w-8 border-t-2" :class="step >= 2 ? 'border-green-600' : 'border-gray-300'"></div>
          <div class="flex items-center" :class="step >= 2 ? 'text-green-600' : 'text-gray-400'">
            <span class="w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium"
                  :class="step >= 2 ? 'border-green-600 bg-green-600 text-white' : 'border-gray-300'">2</span>
            <span class="ml-2 text-sm font-medium">Farm & Location</span>
          </div>
          <div class="w-8 border-t-2" :class="step >= 3 ? 'border-green-600' : 'border-gray-300'"></div>
          <div class="flex items-center" :class="step >= 3 ? 'text-green-600' : 'text-gray-400'">
            <span class="w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium"
                  :class="step >= 3 ? 'border-green-600 bg-green-600 text-white' : 'border-gray-300'">3</span>
            <span class="ml-2 text-sm font-medium">Invite Farmer</span>
          </div>
          <div class="w-8 border-t-2" :class="step >= 4 ? 'border-green-600' : 'border-gray-300'"></div>
          <div class="flex items-center" :class="step >= 4 ? 'text-green-600' : 'text-gray-400'">
            <span class="w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium"
                  :class="step >= 4 ? 'border-green-600 bg-green-600 text-white' : 'border-gray-300'">4</span>
            <span class="ml-2 text-sm font-medium">Complete</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 1: Farmer Information -->
    <div v-if="step === 1" class="py-4 px-4 shadow border-b border-gray-200 bg-white">
      <!-- Duplicate Notification Bar -->
      <div v-if="duplicateNotification.length > 0" class="max-w-2xl mx-auto mb-4">
        <div v-for="duplicate in duplicateNotification" :key="duplicate.field"
             class="mb-2 p-3 rounded-md border-l-4"
             :class="duplicate.type === 'error' ? 'bg-red-50 border-red-400' : 'bg-amber-50 border-amber-400'">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg v-if="duplicate.type === 'error'" class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
              <svg v-else class="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3 flex-1">
              <p class="text-sm" :class="duplicate.type === 'error' ? 'text-red-800' : 'text-amber-800'">
                <strong>{{ duplicate.message }}</strong>.
              </p>
              <div v-if="duplicate.farmer" class="mt-2">
                <a :href="route('app.farmer.view', duplicate.farmer.id)" 
                   target="_blank" 
                   class="text-sm underline"
                   :class="duplicate.type === 'error' ? 'text-red-600 hover:text-red-800' : 'text-amber-600 hover:text-amber-800'">
                  View existing farmer: {{ duplicate.farmer.name }} →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <form class="max-w-2xl mx-auto" @submit.prevent="isEditing ? updateFarmer() : saveAndContinue()">
        <!-- Organisation first, then Farmer Name (admins must select organisation before proceeding) -->
        <div v-if="$page.props.auth.user.role == 'admin'" class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1" for="organisation">
              Organisation <span class="text-red-500">*</span>
            </label>
            <select
              class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              v-model="form.organisation_id"
              @change="selectOrganisationById()"
            >
              <option value="">Select Organisation</option>
              <option
                v-for="organisation in organisations"
                v-bind:key="organisation.hashid"
                v-bind:value="organisation.hashid"
              >
                {{ organisation.name }}
              </option>
            </select>
            <div class="text-red-600 text-sm mt-1" v-if="form.errors.organisation_id">
              {{ form.errors.organisation_id }}
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1" for="name">
              Farmer Name <span class="text-red-500">*</span>
            </label>
            <input
              class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              :class="[
                { 'border-amber-500': duplicateChecks.name.isDuplicate },
                !isOrganisationSelected ? 'bg-gray-100 text-gray-500 placeholder-gray-400 cursor-not-allowed' : ''
              ]"
              id="name"
              type="text"
              autocomplete="name"
              v-model="form.name"
              @input="onNameChange"
              :disabled="!isOrganisationSelected"
            />
            <div class="text-red-600 text-sm mt-1" v-if="form.errors.name">
              {{ form.errors.name }}
            </div>
          </div>
        </div>
        <!-- Disabled gate for remaining fields until organisation chosen (admins only) -->
        <fieldset :disabled="isAdmin && !isOrganisationSelected" :class="{ 'opacity-50 grayscale pointer-events-none': isAdmin && !isOrganisationSelected }">

  <!-- Farmer Name (for non-admins) -->
  <div v-if="!isAdmin" class="mb-3">
          <label class="block text-sm font-medium text-gray-700 mb-1" for="name">
            Farmer Name <span class="text-red-500">*</span>
          </label>
          <input
            class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            :class="{ 'border-amber-500': duplicateChecks.name.isDuplicate }"
            id="name"
            type="text"
            autocomplete="name"
            v-model="form.name"
            @input="onNameChange"
          />
          <!-- Duplicate warning -->
          <div class="flex items-center text-amber-600 text-sm mt-1" v-if="duplicateChecks.name.loading">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Checking for duplicates...
          </div>
          <div class="text-red-600 text-sm mt-1" v-if="form.errors.name">
            {{ form.errors.name }}
          </div>
        </div>

        <!-- Enhanced Eircode Lookup Section -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
          <h3 class="text-sm font-medium text-blue-900 mb-2"> Address Details <span class="text-red-500">*</span></h3>
          <p class="text-xs text-blue-700 mb-2">Enter eircode for auto-fill or manual entry. Address required.</p>
          
          <div class="flex flex-col sm:flex-row sm:items-end gap-2">
            <div class="flex-1">
              <label class="block text-xs font-medium text-gray-700 mb-1" for="eircode">
                Eircode
              </label>
              <div class="flex rounded-md shadow-sm">
                <input
                  class="focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-none rounded-l-md text-sm border-gray-300"
                  id="eircode"
                  type="text"
                  autocomplete="postal-code"
                  v-model="form.eircode"
                  placeholder="e.g., A65 F4E2"
                  @input="onEircodeChange"
                />
                <button
                  type="button"
                  @click="lookupEircode"
                  :disabled="!form.eircode || eircodeLoading"
                  class="relative -ml-px inline-flex items-center rounded-r-md border border-gray-300 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>{{ eircodeLoading ? '...' : 'Lookup' }}</span>
                </button>
              </div>
            </div>
            
            <button
              type="button"
              @click="showManualAddress = !showManualAddress"
              class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {{ showManualAddress ? '✕ Hide' : (form.address_line_1 || form.city || form.county) ? 'Edit' : 'Add Manually' }}
            </button>
          </div>
          
          <div class="text-red-600 text-sm mt-2" v-if="form.errors.eircode">
            {{ form.errors.eircode }}
          </div>
          
          <div class="text-red-600 text-sm mt-2" v-if="eircodeError">
            {{ eircodeError }}
          </div>

          <!-- Warning when no address is provided -->
          <div v-if="!showManualAddress && !form.address_line_1 && !form.city && !form.county" class="mt-2 p-2 bg-amber-50 border-l-4 border-amber-400 rounded">
            <p class="text-xs text-amber-700">⚠️ <strong>Address required:</strong> Enter eircode or use manual entry</p>
          </div>

          <!-- Display populated address after successful lookup -->
          <div v-if="!showManualAddress && (form.address_line_1 || form.city || form.county)" class="mt-2 p-2 bg-green-50 border-l-4 border-green-400 rounded">
            <div class="text-xs text-green-700">
              <span v-if="form.address_line_1">{{ form.address_line_1 }}</span><span v-if="form.address_line_2">, {{ form.address_line_2 }}</span><span v-if="form.city">, {{ form.city }}</span><span v-if="form.county">, {{ form.county }}</span><span v-if="form.eircode">, {{ form.eircode }}</span>
            </div>
          </div>

          <!-- Manual Address Fields (Show only when user explicitly requests) -->
          <div v-if="showManualAddress" class="mt-2 border-t border-blue-200 pt-2">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1" for="address_line_1">
                  Address Line 1 <span class="text-red-500">*</span>
                </label>
                <input
                  class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm text-sm border-gray-300 rounded-md"
                  id="address_line_1"
                  type="text"
                  required
                  autocomplete="address-line1"
                  v-model="form.address_line_1"
                />
                <div class="text-red-600 text-xs mt-1" v-if="form.errors.address_line_1">
                  {{ form.errors.address_line_1 }}
                </div>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1" for="address_line_2">
                  Address Line 2
                </label>
                <input
                  class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm text-sm border-gray-300 rounded-md"
                  id="address_line_2"
                  type="text"
                  autocomplete="address-line2"
                  v-model="form.address_line_2"
                />
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1" for="city">
                  Town/City
                </label>
                <input
                  class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm text-sm border-gray-300 rounded-md"
                  id="city"
                  type="text"
                  autocomplete="address-level2"
                  v-model="form.city"
                />
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1" for="county">
                  County
                </label>
                <select
                  class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm text-sm border-gray-300 rounded-md"
                  id="county"
                  v-model="form.county"
                >
                  <option value="">Select County</option>
                  <option
                    v-for="(county, key) in counties"
                    :value="county"
                    :key="key"
                  >
                    {{ county }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact & Farm Details - Comfortable Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 mt-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2" for="contact_name">
              Contact Name
            </label>
            <input
              class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md py-2"
              id="contact_name"
              type="text"
              autocomplete="name"
              v-model="form.contact_name"
            />
            <div class="text-red-600 text-sm mt-1" v-if="form.errors.contact_name">
              {{ form.errors.contact_name }}
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2" for="account_number">
              {{ accountNoLabel }}
              <span v-if="isAccountNoRequired" class="text-red-500">*</span>
            </label>
            <input
              class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md py-2"
              :class="{ 
                'border-red-500': isAccountNoRequired && form.account_number && !isDairygoldAccountValid,
                'border-amber-500': duplicateChecks.account_number.isDuplicate
              }"
              id="account_number"
              type="text"
              autocomplete="off"
              v-model="form.account_number"
              :required="isAccountNoRequired"
              :placeholder="isAccountNoRequired ? 'e.g., 01234567' : ''"
              @input="onAccountNumberChange"
            />
            <!-- Helper text for Dairygold -->
            <p v-if="isAccountNoRequired" class="text-xs text-gray-500 mt-1">
              Must be exactly 8 digits starting with 0 (e.g., 01234567)
            </p>
            <!-- Duplicate checking loading -->
            <div class="flex items-center text-amber-600 text-sm mt-1" v-if="duplicateChecks.account_number.loading">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Checking for duplicates...
            </div>

            <!-- Validation error for account number -->
            <div class="text-red-600 text-sm mt-1" v-if="form.errors.account_number">
              {{ form.errors.account_number }}
            </div>
            <!-- Custom validation error for Dairygold format -->
            <div class="text-red-600 text-sm mt-1" v-if="isAccountNoRequired && form.account_number && !isDairygoldAccountValid">
              Account number must be exactly 8 digits starting with 0
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2" for="mobile">
              Phone
            </label>
            <input
              class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md py-2"
              id="mobile"
              type="tel"
              autocomplete="tel"
              v-model="form.mobile"
            />
            <div class="text-red-600 text-sm mt-1" v-if="form.errors.mobile">
              {{ form.errors.mobile }}
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2" for="email">
              Email
            </label>
            <input
              class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md py-2"
              id="email"
              type="email"
              autocomplete="email"
              v-model="form.email"
            />
            <div class="text-red-600 text-sm mt-1" v-if="form.errors.email">
              {{ form.errors.email }}
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2" for="herd_no">
              Herd No.
            </label>
            <input
              class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md py-2"
              :class="{ 'border-amber-500': duplicateChecks.herd_no.isDuplicate }"
              id="herd_no"
              type="text"
              v-model="form.herd_no"
              @input="onHerdNoChange"
            />
            <!-- Duplicate checking loading -->
            <div class="flex items-center text-amber-600 text-sm mt-1" v-if="duplicateChecks.herd_no.loading">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Checking for duplicates...
            </div>

            <div class="text-red-600 text-sm mt-1" v-if="form.errors.herd_no">
              {{ form.errors.herd_no }}
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2" for="farm_type">
              Farm Type
            </label>
            <select
              class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md py-2"
              id="farm_type"
              v-model="form.farm_type"
            >
              <option value="">Select Type</option>
              <option
                v-for="type in farm_types"
                v-bind:key="type.value"
                v-bind:value="type.value"
              >
                {{ type.label }}
              </option>
            </select>
            <div class="text-red-600 text-sm mt-1" v-if="form.errors.farm_type">
              {{ form.errors.farm_type }}
            </div>
          </div>
        </div>

        <!-- Consultant Assignment (if applicable) -->
        <div
          v-if="['admin', 'consultant'].includes($page.props.auth.user.role)"
          class="mb-3"
        >
          <label class="block text-xs font-medium text-gray-700 mb-1" for="consultant_user">
            Assign Consultant
          </label>
          <select
            class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm text-sm border-gray-300 rounded-md py-1"
            id="consultant_user"
            v-model="form.consultant_user"
          >
            <option value="">Select consultant</option>
            <option
              v-for="consultant in consultants"
              v-bind:key="consultant.hashid"
              v-bind:value="consultant.id"
            >
              {{ consultant.first_name + " " + consultant.last_name }}
            </option>
          </select>
          <div class="text-red-600 text-xs" v-if="form.errors.consultant_user">
            {{ form.errors.consultant_user }}
          </div>
        </div>

        <!-- Schemes & Programmes -->
        <div class="mb-4">
          <h3 class="text-sm font-medium text-gray-700 mb-3">Schemes & Programmes</h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="bg-white border border-gray-200 rounded-lg p-3">
              <label class="block text-xs font-medium text-gray-700 mb-2">Derogation</label>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="px-3 py-1 border text-sm font-medium rounded shadow-sm flex-1"
                  :class="form.derogation == 1 && form.derogation != null ? 'border-transparent bg-green-600 text-white' : 'border-gray-300 bg-white text-gray-700'"
                  @click="setDerogation(1)"
                >YES</button>
                <button
                  type="button"
                  class="px-3 py-1 border text-sm font-medium rounded shadow-sm flex-1"
                  :class="form.derogation == 0 && form.derogation != null ? 'border-transparent bg-red-600 text-white' : 'border-gray-300 bg-white text-gray-700'"
                  @click="setDerogation(0)"
                >NO</button>
              </div>
            </div>

            <div class="bg-white border border-gray-200 rounded-lg p-3">
              <label class="block text-xs font-medium text-gray-700 mb-2">Organic</label>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="px-3 py-1 border text-sm font-medium rounded shadow-sm flex-1"
                  v-bind:class="
                    form.organic == 1 && form.organic != null
                      ? 'border-transparent bg-green-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700'
                  "
                  v-on:click="setOrganic(1)"
                >
                  YES
                </button>
                <button
                  type="button"
                  class="px-3 py-1 border text-sm font-medium rounded shadow-sm flex-1"
                  v-bind:class="
                    form.organic == 0 && form.organic != null
                      ? 'border-transparent bg-red-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700'
                  "
                  v-on:click="setOrganic(0)"
                >
                  NO
                </button>
              </div>
            </div>

            <div class="bg-white border border-gray-200 rounded-lg p-3">
              <label class="block text-xs font-medium text-gray-700 mb-2">Co-op Sustainability Programme</label>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="px-3 py-1 border text-sm font-medium rounded shadow-sm flex-1"
                  v-bind:class="
                    form.coop_program == 1 && form.coop_program != null
                      ? 'border-transparent bg-green-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700'
                  "
                  v-on:click="setCoopProgram(1)"
                >
                  YES
                </button>
                <button
                  type="button"
                  class="px-3 py-1 border text-sm font-medium rounded shadow-sm flex-1"
                  v-bind:class="
                    form.coop_program == 0 && form.coop_program != null
                      ? 'border-transparent bg-red-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700'
                  "
                  v-on:click="setCoopProgram(0)"
                >
                  NO
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Account Status -->
        <div class="mb-4 p-3 bg-gray-50 rounded border">
          <label for="enabled" class="flex items-center">
            <input
              class="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              id="enabled"
              type="checkbox"
              v-model="form.enabled"
            />
            <span class="ml-2 text-sm text-gray-700">Active Account</span>
          </label>
          <p class="text-xs text-gray-500 mt-1 ml-6">Enable or disable this farmer's account</p>
        </div>

        <div class="flex justify-between items-center mt-4 pt-3 border-t border-gray-200">
          <button type="button" @click="onCancel" class="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Cancel
          </button>
          <button
            type="button"
            @click="saveAndContinue"
            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            :class="[
              (form.processing || !canProceedToStep2) 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-indigo-600 hover:bg-indigo-700'
            ]"
            :disabled="form.processing || !canProceedToStep2"
            :title="submitButtonTooltip"
          >
            <span v-if="form.processing">Processing...</span>
            <span v-else-if="!canProceedToStep2">Complete Required Fields</span>
            <span v-else>{{ submitButtonText }}</span>
          </button>
        </div>
        </fieldset>
      </form>
    </div>

    <!-- Step 2: Farm Setup & Location -->
    <div v-if="step === 2 && !isEditing" class="py-8 px-5 shadow border-b border-gray-200 bg-white">
      <div class="text-center mb-6">
        <h2 class="text-2xl font-bold text-gray-900">Add Your First Farm</h2>
        <p class="text-gray-600 mt-2">Enter the farm name and set its location.</p>
        
        <!-- Show status if farmer already created -->
        <div v-if="createdFarmer" class="mt-3 p-3 bg-green-50 border border-green-200 rounded-md max-w-md mx-auto">
          <p class="text-green-800 text-sm">✅ Farmer "{{ form.name }}" has been created successfully!</p>
        </div>
        
        <div class="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md max-w-2xl mx-auto">
          <h3 class="text-sm font-medium text-blue-800 mb-2">Why is this required?</h3>
          <p class="text-xs text-blue-700">Farms with locations are essential for:</p>
          <ul class="text-xs text-blue-700 mt-1 space-y-1">
            <li>• Creating and managing soil sampling tasks</li>
            <li>• Generating accurate reports and recommendations</li>
            <li>• Mapping and GPS-based operations</li>
          </ul>
        </div>
      </div>
      
      <div class="max-w-md mx-auto space-y-6">
        <!-- Farm Name Section -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Farm Name</label>
          <input 
            v-model="farmForm.name" 
            type="text" 
            class="w-full rounded border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            placeholder="e.g., Homefarm, Main Farm, etc."
            required
          />
          <p class="text-xs text-gray-500 mt-1">💡 We've suggested "Homefarm" as a common name for the main farm, but you can change this to anything you prefer.</p>
          <div class="text-red-600 text-sm mt-1" v-if="farmErrors.name">
            {{ farmErrors.name }}
          </div>
        </div>

        <!-- Farm Location Section -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Farm Location</label>
          <div class="p-4 border border-gray-300 rounded-lg bg-gray-50">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-sm font-medium text-gray-700">{{ farmForm.name || 'Your Farm' }}</h3>
              <div v-if="farmForm.location.length" class="text-green-600">
                <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                Location Set
              </div>
            </div>

            <div v-if="!farmForm.location.length" class="mb-4">
              <div class="p-3 bg-blue-50 border border-blue-200 rounded">
                <div v-if="eircodeCoordinates">
                  <p class="text-blue-800 text-sm">📍 We'll use your eircode location ({{ form.eircode }}) as the default farm location</p>
                  <p class="text-blue-700 text-xs mt-1">You can adjust the exact position on the map if needed</p>
                </div>
                <div v-else>
                  <p class="text-blue-800 text-sm">📍 Click the button below to set the farm location on the map</p>
                </div>
              </div>
            </div>
            <div v-else class="mb-4">
              <div class="p-3 bg-green-50 border border-green-200 rounded">
                <p class="text-green-800 text-sm">✅ Farm location has been set successfully!</p>
              </div>
            </div>

            <button 
              type="button" 
              @click="addFarmPoint('farm-location')" 
              class="w-full py-3 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#1E660B] hover:bg-[#165509] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1E660B]"
              :class="{ 'opacity-50': showFormOverlay }"
              :disabled="showFormOverlay"
            >
              <span v-if="showFormOverlay">Click on the map to set location...</span>
              <span v-else-if="farmForm.location.length && eircodeCoordinates">Adjust Location</span>
              <span v-else-if="farmForm.location.length">Update Farm Location</span>
              <span v-else>Set Farm Location on Map</span>
            </button>
          </div>
        </div>
        
        <div class="flex justify-between">
          <button type="button" @click="step = 1" class="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Back
          </button>
          <button type="button" @click="completeFarmSetup" class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" :disabled="!farmForm.name || !farmForm.location.length || farmSetupProcessing">
            <span v-if="farmSetupProcessing">Creating Farmer & Farm...</span>
            <span v-else-if="createdFarmer">Continue to Next Step</span>
            <span v-else>Continue to Next Step</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Step 3: Invite Farmers -->
    <div v-if="step === 3 && !isEditing" class="py-8 px-5 shadow border-b border-gray-200 bg-white">
      <div class="text-center mb-6">
        <h2 class="text-2xl font-bold text-gray-900">Invite Farmers</h2>
        <p class="text-gray-600 mt-2">Invite {{ form.name }} to set up their Livefarm account.</p>

        <div class="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md max-w-2xl mx-auto">
          <h3 class="text-sm font-medium text-blue-800 mb-2">Why invite farmers?</h3>
          <p class="text-xs text-blue-700">Invited users can:</p>
          <ul class="text-xs text-blue-700 mt-1 space-y-1">
            <li>• Direct access to their data and reports</li>
            <li>• Create and manage tasks</li>
            <li>• Collaborate on farm management activities</li>
          </ul>
        </div>
      </div>
      
      <form class="max-w-2xl mx-auto">
        <!-- Users Section -->
        <div v-for="(user, index) in form.users" :key="index" class="mb-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
          <div class="flex justify-between items-center mb-2">
            <h4 class="text-sm font-medium text-gray-700">User {{ index + 1 }}</h4>
            <button
              type="button"
              @click="removeUser(index)"
              class="text-red-600 hover:text-red-800 text-sm"
            >
              Remove
            </button>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label
                class="block text-sm font-medium text-gray-700 mb-1"
                :class="{
                  'text-red-600': form.errors['users.' + index + '.first_name'],
                }"
                :for="'first_name_' + index"
              >
                First name
              </label>
              <input
                class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md py-2"
                :class="{
                  'border-red-600':
                    form.errors['users.' + index + '.first_name'],
                }"
                :id="'first_name_' + index"
                type="text"
                autocomplete="given-name"
                v-model="form.users[index].first_name"
              />
              <div
                class="text-red-600 text-sm mt-1"
                v-if="form.errors['users.' + index + '.first_name']"
              >
                {{ form.errors["users." + index + ".first_name"] }}
              </div>
            </div>

            <div>
              <label
                class="block text-sm font-medium text-gray-700 mb-1"
                :class="{
                  'text-red-600': form.errors['users.' + index + '.last_name'],
                }"
                :for="'last_name_' + index"
              >
                Last name
              </label>
              <input
                class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md py-2"
                :class="{
                  'border-red-600':
                    form.errors['users.' + index + '.last_name'],
                }"
                :id="'last_name_' + index"
                type="text"
                autocomplete="family-name"
                v-model="form.users[index].last_name"
              />
              <div
                class="text-red-600 text-sm mt-1"
                v-if="form.errors['users.' + index + '.last_name']"
              >
                {{ form.errors["users." + index + ".last_name"] }}
              </div>
            </div>

            <div>
              <label
                class="block text-sm font-medium text-gray-700 mb-1"
                :class="{
                  'text-red-600': form.errors['users.' + index + '.email'],
                }"
                :for="'email_' + index"
              >
                Email
              </label>
              <input
                class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md py-2"
                :class="{
                  'border-red-600': form.errors['users.' + index + '.email'],
                }"
                :id="'email_' + index"
                type="email"
                autocomplete="email"
                v-model="form.users[index].email"
              />
              <div
                class="text-red-600 text-sm mt-1"
                v-if="form.errors['users.' + index + '.email']"
              >
                {{ form.errors["users." + index + ".email"] }}
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-start mb-6">
          <button
            type="button"
            @click="addUser"
            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            :disabled="form.processing"
          >
            <svg
              class="-ml-1 mr-2 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                clip-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                fill-rule="evenodd"
              />
            </svg>
            Add User
          </button>
        </div>

        <!-- Validation message for user invitations -->
        <div v-if="!hasCompleteUser && form.users.length > 0" class="mt-4 p-3 bg-amber-50 border-l-4 border-amber-400 rounded">
          <p class="text-amber-700 text-sm">⚠️ <strong>Complete user details:</strong> Please fill in first name, last name, and email for at least one user to send invitations.</p>
        </div>

        <div class="flex justify-between items-center mt-6 pt-3 border-t border-gray-200">
          <button type="button" @click="step = 2" class="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Back
          </button>
          <div class="flex gap-3">
            <button type="button" @click="skipUsers" :disabled="invitesProcessing" class="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
              Skip Invitations
            </button>
            <button
              type="button"
              @click="completeSetup"
              class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              :class="[
                (invitesProcessing || !hasCompleteUser)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              ]"
              :disabled="invitesProcessing || !hasCompleteUser"
              :title="!hasCompleteUser ? 'Please add at least one user with complete details to send invitations' : ''"
            >
              <span v-if="invitesProcessing">Sending Invitations...</span>
              <span v-else-if="!hasCompleteUser">Add User Details to Continue</span>
              <span v-else>Finish & Send Invitations</span>
            </button>
          </div>
        </div>
      </form>
    </div>

    <!-- Step 4: Complete Setup -->
    <div v-if="step === 4 && !isEditing" class="py-8 px-5 shadow border-b border-gray-200 bg-white">
      <div class="text-center">
        <div class="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Setup Complete!</h2>
        <p class="text-gray-600 mb-2">{{ form.name }} has been created with farm "{{ farmForm.name }}" and location set.</p>
        <p class="text-gray-600 mb-6">{{ invitesSent ? invitationCount(invitesSent) + ' emailed.' : 'No user invitations were sent.' }}</p>
        

        <!-- Optional next steps -->
        <div class="bg-gray-50 border border-gray-200 rounded p-4 mb-6 max-w-lg mx-auto">
          <h3 class="text-sm font-medium text-gray-800 mb-2">What's Next? (Optional)</h3>
          <p class="text-xs text-gray-600 mb-3">You can continue setting up this farmer or start working with tasks right away.</p>
          <ul class="text-xs text-gray-600 space-y-1">
            <li>• <strong>Draw Parcels:</strong> Manually define field boundaries within the farm for detailed mapping</li>
            <li>• <strong>Import Parcels:</strong> Import existing field boundaries from GeoJSON files (NMP compatible)</li>
            <li v-if="canLinkLpis">• <strong>Link LPIS Parcels:</strong> Select the farmer's parcels on the DAFM LPIS map</li>
            <li>• <strong>Create Tasks:</strong> Start soil sampling or other agricultural tasks immediately</li>
            <li>• <strong>View Profile:</strong> Review and edit the farmer's complete information</li>
          </ul>
        </div>

        <div class="flex flex-wrap justify-center gap-3">
          <button type="button" @click="viewFarmer" class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            View Farmer Profile
          </button>
          <button type="button" @click="addParcels" v-if="createdFarmer?.hashid" class="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Draw Parcels for {{ farmForm.name }}
          </button>
          <button type="button" @click="importParcels" v-if="createdFarmer?.hashid" class="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Import Parcels from GeoJSON
          </button>
          <button type="button" @click="linkLpisParcels" v-if="createdFarmer?.hashid && canLinkLpis" class="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Link LPIS Parcels
          </button>
          <inertia-link v-if="createdFarmer?.hashid" :href="route('app.task.create', createdFarmer.hashid)" class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
            Create Task
          </inertia-link>
        </div>
      </div>
    </div>

    <div
      v-if="$page.props.flash.message"
      class="font-bold"
      :class="
        $page.props.flash.status == 'success'
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-700'
      "
    >
      {{ $page.props.flash.message }}
    </div>
  </div>
</template>

<script>
import { canLinkDafmLpis } from "@/Partials/Map/Util/DafmLpisPage";
import AppLayout from "@/Layouts/App";
import { useForm } from "@inertiajs/inertia-vue3";
import _ from "lodash";
import axios from "axios";
import { useToast } from "vue-toastification";
import { inviteFarmerUsers } from "@/Composables/useFarmerInvites";

export default {
  layout: [AppLayout],
  props: {
    farmer: {
      type: Object,
      default: null
    },
    organisations: Object,
    counties: Array,
    current_organisation: Object,
    farm_types: Array,
    // Optional redirect target (e.g., 'view') to control post-update navigation
    redirectTo: {
      type: String,
      default: null
    },
    // Pre-populated name from URL parameter
    name: {
      type: String,
      default: null
    }
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      step: 1,
      currentOrganisation: {},
      consultants: [],
      form: null,
      createdFarmer: null,
      farmForm: {
        name: 'Homefarm',
        location: [],
        location_xy: []
      },
      farmErrors: {},
      farmSetupProcessing: false,
      invitesProcessing: false,
      invitesSent: 0,
      showFormOverlay: false,
      allowSubmit: false,
      toast: null,
      eircodeLoading: false,
      eircodeError: null,
      eircodeCoordinates: null,
      showManualAddress: false,
      // Duplicate checking
      duplicateChecks: {
        name: { isDuplicate: false, loading: false },
        account_number: { isDuplicate: false, loading: false },
        herd_no: { isDuplicate: false, loading: false }
      },
    };
  },

  computed: {
    canLinkLpis() {
      // form.organisation starts as {}, so fall back by name, and offer
      // nothing until the organisation is known
      const name = this.form.organisation?.name || this.currentOrganisation?.name;
      return !!name && canLinkDafmLpis(this.$page.props.config, name);
    },
    isEditing() {
      return this.farmer !== null;
    },

    pageTitle() {
      return this.isEditing ? 'Edit Farmer' : 'Add Farmer';
    },

    submitButtonText() {
      return this.isEditing ? 'Update Farmer' : 'Continue to Farm Setup';
    },

    submitButtonTooltip() {
      if (this.canProceedToStep2) return '';
      return this.isEditing 
        ? 'Please enter farmer name and address to update' 
        : 'Please enter farmer name and address to continue';
    },

    canProceedToStep2() {
      let hasRequiredFields = this.form.name && this.form.address_line_1;
      
      // Check if account number is required and valid (for Dairygold)
      if (this.isAccountNoRequired) {
        hasRequiredFields = hasRequiredFields && this.isDairygoldAccountValid;
      }
      
      // Check for critical duplicates - only these block progression (not name duplicates) 
      if (this.hasCriticalDuplicates) {
        return false;
      }
      
      // For admin users, organisation is also required
      if (this.$page.props.auth.user.role === 'admin') {
        // Check if organisation is selected (either object with hashid or organisation_id is set)
        const hasOrganisation = (this.form.organisation && this.form.organisation.hashid) || this.form.organisation_id;
        return hasRequiredFields && hasOrganisation;
      }
      
      return hasRequiredFields;
    },
    isAdmin() {
      return this.$page.props.auth.user.role === 'admin';
    },
    isOrganisationSelected() {
      return (this.form.organisation && this.form.organisation.hashid) || this.form.organisation_id;
    },

    accountNoLabel() {
      if (this.currentOrganisation && this.currentOrganisation.name) {
        return `${this.currentOrganisation.name} Account No.`;
      }
      return 'Account No.';
    },

    isAccountNoRequired() {
      return this.currentOrganisation && (
        (this.currentOrganisation.name && this.currentOrganisation.name.toLowerCase().includes('dairygold')) ||
        this.currentOrganisation.id === 4
      );
    },

    isDairygoldOrganisation() {
      return this.currentOrganisation && 
        this.currentOrganisation.name && 
        this.currentOrganisation.name.toLowerCase().includes('dairygold');
    },
    
    hasCompleteUser() {
      // Check if at least one user has all required fields filled
      return this.form.users.some(user => 
        user.first_name && user.first_name.trim() && 
        user.last_name && user.last_name.trim() && 
        user.email && user.email.trim()
      );
    },
    
    isDairygoldAccountValid() {
      // If account number is not required, it's always valid
      if (!this.isAccountNoRequired) return true;
      
      // If required but empty, it's invalid
      if (!this.form.account_number) return false;
      
      // Only apply Dairygold format validation for actual Dairygold organizations
      if (this.isDairygoldOrganisation) {
        // Dairygold account number must be exactly 8 digits starting with 0
        const accountPattern = /^0\d{7}$/;
        return accountPattern.test(this.form.account_number);
      }
      
      // For other organizations (like org ID 4), any non-empty account number is valid
      return true;
    },

    duplicateNotification() {
      const duplicates = [];
      
      if (this.duplicateChecks.name.isDuplicate) {
        duplicates.push({
          type: 'warning',
          field: 'name',
          message: 'A farmer with this name already exists',
          farmer: this.duplicateChecks.name.existingFarmer,
          critical: false
        });
      }
      
      if (this.duplicateChecks.account_number.isDuplicate) {
        duplicates.push({
          type: 'error',
          field: 'account_number', 
          message: `A farmer with this account no. already exists`,
          farmer: this.duplicateChecks.account_number.existingFarmer,
          critical: true
        });
      }
      
      if (this.duplicateChecks.herd_no.isDuplicate) {
        duplicates.push({
          type: 'error',
          field: 'herd_no',
          message: 'A farmer with this herd number already exists', 
          farmer: this.duplicateChecks.herd_no.existingFarmer,
          critical: true
        });
      }
      
      return duplicates;
    },

    hasCriticalDuplicates() {
      return this.duplicateNotification.some(d => d.critical);
    }
  },

  created() {
    // Initialize the form
    this.initializeForm();
    // Load organisation and consultants based on current state
    if (this.isEditing && this.form?.organisation_id) {
      this.selectOrganisationById();
    } else {
      this.selectOrganisation();
    }
  },

  mounted() {
    // Listen for map interaction events - same as FarmWidget
    this.$emitter.on("argicsMapLocationUseOldDetails", (payload) => {
      if (payload.status == true) {
        this.allowSubmit = true;
      }
    });

    // Listen for the location data from the map interaction
    this.$emitter.on("argicsMapLocation", (payload) => {
      this.$root.showMapOnMobile = false;
      this.showFormOverlay = false;
      
      if (payload.action === "farm-location") {
        this.farmForm.location = payload.identifier;
        this.farmForm.location_xy = payload.identifier_xy;
        
        // Show a marker at the new location
        this.$emitter.emit("showLocationMarker", {
          coordinates: payload.identifier,
          type: "farm-location"
        });
      }

      this.allowSubmit = true;
    });
  },

  methods: {
    onCancel() {
      // If editing and originated from view page, go back to that view
      if (this.isEditing && this.redirectTo === 'view' && this.farmer && (this.farmer.hashid || this.farmer.id)) {
        const id = this.farmer.hashid || this.farmer.id;
        this.$inertia.visit(route('app.farmer.view', id));
        return;
      }
      // Default cancel action: go to index
      this.$inertia.visit(route('app.farmer.index'));
    },
    initializeForm() {
      // Create form with default empty values
      const formData = {
        name: this.name || "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        county: "",
        eircode: "",
        contact_name: "",
        account_number: "",
        mobile: "",
        email: "",
        consultant_user: "",
        enabled: true,
        derogation: null,
        organic: null,
        coop_program: null,
        organisation_id: "",
        farm_type: "",
        organisation: {},
        herd_no: "",
        users: [
          {
            first_name: "",
            last_name: "",
            email: "",
          },
        ],
      };

      // If editing, populate with existing farmer data
      if (this.isEditing) {
        Object.keys(formData).forEach(key => {
          if (this.farmer[key] !== undefined) {
            formData[key] = this.farmer[key];
          }
        });

        // Handle special cases for nested objects/arrays
        if (this.farmer.organisation) {
          formData.organisation = this.farmer.organisation;
          formData.organisation_id = this.farmer.organisation.hashid || this.farmer.organisation.id;
        }

        if (this.farmer.consultant) {
          formData.consultant_user = this.farmer.consultant.id;
        }

        // Handle enabled field - it might be named differently or need conversion
        if (this.farmer.enabled !== undefined) {
          formData.enabled = !!this.farmer.enabled; // Convert to boolean
        } else if (this.farmer.active !== undefined) {
          formData.enabled = !!this.farmer.active; // Alternative field name
        } else if (this.farmer.status !== undefined) {
          formData.enabled = this.farmer.status === 'active' || this.farmer.status === 1;
        }

        // Debug logging to help identify the issue
        console.log('Farmer enabled status:', {
          farmer_enabled: this.farmer.enabled,
          farmer_active: this.farmer.active,
          farmer_status: this.farmer.status,
          final_enabled: formData.enabled
        });
      }

      this.form = useForm(formData);
    },

    setOrganic(status) {
      if (this.form.organic == status) {
        this.form.organic = null;
      } else {
        this.form.organic = status;
      }
    },
    setDerogation(status) {
      if (this.form.derogation === status) {
        this.form.derogation = null;
      } else {
        this.form.derogation = status;
      }
    },
    setCoopProgram(status) {
      if (this.form.coop_program == status) {
        this.form.coop_program = null;
      } else {
        this.form.coop_program = status;
      }
    },
    addUser() {
      this.form.users.push({
        first_name: "",
        last_name: "",
        email: "",
      });
    },
    removeUser(index) {
      if (this.form.users.length == 1) {
        this.form.users[index].first_name = "";
        this.form.users[index].last_name = "";
        this.form.users[index].email = "";

        return;
      }
      this.form.users.splice(index, 1);
    },

    // Eircode functionality
    onEircodeChange() {
      // Clear any previous errors when user types
      this.eircodeError = null;
    },

    async lookupEircode() {
      if (!this.form.eircode) return;

      this.eircodeLoading = true;
      this.eircodeError = null;

      try {
        // Use our backend ArcGIS geocoding API
        const response = await axios.post('/api/geocoding/eircode', {
          eircode: this.form.eircode
        });

        if (response.data.success) {
          const data = response.data;
          let addressFieldsPopulated = false;
          
          // Store coordinates for map zoom later
          this.eircodeCoordinates = {
            lat: data.coordinates.lat,
            lng: data.coordinates.lng
          };
          
          // Fill in the address fields from ArcGIS response
          if (data.address.address_line_1) {
            this.form.address_line_1 = data.address.address_line_1;
            addressFieldsPopulated = true;
          }
          
          if (data.address.city) {
            this.form.city = data.address.city;
            addressFieldsPopulated = true;
          }
          
          if (data.address.region) {
            // Match ArcGIS region to our county options
            const regionName = data.address.region;
            const matchingCounty = this.counties.find(county => 
              county.toLowerCase().includes(regionName.toLowerCase()) ||
              regionName.toLowerCase().includes(county.toLowerCase())
            );
            if (matchingCounty) {
              this.form.county = matchingCounty;
              addressFieldsPopulated = true;
            }
          }

          // Only show success message if address fields were actually populated
          if (addressFieldsPopulated) {
            this.$swal.fire({
              icon: 'success',
              title: 'Address Found!',
              text: 'Address details have been filled in from your eircode.',
              timer: 2000,
              showConfirmButton: false
            });
          } else {
            this.eircodeError = 'Location found but no address details available for this eircode.';
          }

        } else {
          this.eircodeError = response.data.error || 'No address found for this eircode.';
        }

      } catch (error) {
        console.error('Eircode lookup error:', error);
        
        if (error.response) {
          // Server responded with error
          if (error.response.status === 422) {
            this.eircodeError = 'Invalid eircode format. Please check and try again.';
          } else if (error.response.data && error.response.data.error) {
            this.eircodeError = error.response.data.error;
          } else {
            this.eircodeError = 'Unable to lookup address. Please try again.';
          }
        } else {
          // Network or other error
          this.eircodeError = 'Unable to lookup address. Please check your connection and try again.';
        }
      } finally {
        this.eircodeLoading = false;
      }
    },

    selectOrganisationById() {
      // For admin users: find the selected organisation by ID
      if (this.$page.props.auth.user.role === 'admin') {
        if (this.organisations && this.form.organisation_id) {
          const selectedOrg = this.organisations.find(org => org.hashid === this.form.organisation_id);
          if (selectedOrg) {
            this.form.organisation = selectedOrg;
            this.currentOrganisation = selectedOrg;
          }
        }
      } else {
        // For non-admin users: use their current organisation
        this.form.organisation_id = this.current_organisation.hashid;
        this.currentOrganisation = this.current_organisation;
        this.form.organisation = this.current_organisation;
      }

      // Load consultants for the selected organisation
      if (this.form.organisation_id) {
        axios
          .get(
            route("app.organisation.members", { organisation: this.form.organisation_id })
          )
          .then((response) => {
            this.consultants = response.data.data;
          });
      }
    },

    selectOrganisation() {
      // For admin users: use selected organisation from dropdown
      if (this.$page.props.auth.user.role === 'admin') {
        if (this.organisations && this.form.organisation) {
          this.form.organisation_id = this.form.organisation.hashid;
          this.currentOrganisation = this.form.organisation;
        }
      } else {
        // For non-admin users: use their current organisation
        this.form.organisation_id = this.current_organisation.hashid;
        this.currentOrganisation = this.current_organisation;
        this.form.organisation = this.current_organisation;
      }

      // Load consultants for the selected organisation
      if (this.form.organisation?.hashid || this.form.organisation_id) {
        let organisation =
          this.form.organisation?.hashid || this.form.organisation_id;
        axios
          .get(
            route("app.organisation.members", { organisation: organisation })
          )
          .then((response) => {
            this.consultants = response.data.data;
          });
      }
    },

    saveAndContinue() {
      // Validate required fields first
      if (!this.canProceedToStep2) {
        let missingFields = [];
        if (!this.form.name) missingFields.push('Farmer Name');
        if (!this.form.address_line_1) missingFields.push('Address');
        
        // Check if account number is required and valid
        if (this.isAccountNoRequired && !this.isDairygoldAccountValid) {
          if (!this.form.account_number) {
            missingFields.push(this.accountNoLabel);
          } else {
            // Show format requirement only for Dairygold organizations
            if (this.isDairygoldOrganisation) {
              missingFields.push(`Valid ${this.accountNoLabel} (must be 8 digits starting with 0)`);
            } else {
              missingFields.push(`Valid ${this.accountNoLabel}`);
            }
          }
        }
        
        // For admin users, check organisation requirement
        if (this.$page.props.auth.user.role === 'admin') {
          const hasOrganisation = (this.form.organisation && this.form.organisation.hashid) || this.form.organisation_id;
          if (!hasOrganisation) {
            missingFields.push('Organisation');
          }
        }
        
        // Add visual feedback for required fields
        this.$swal.fire({
          icon: 'warning',
          title: 'Required Fields Missing',
          text: `Please fill in the following required fields: ${missingFields.join(', ')}.`,
          confirmButtonText: 'OK'
        });
        
        // If address is missing and manual fields are hidden, show them
        if (!this.form.address_line_1 && !this.showManualAddress) {
          this.showManualAddress = true;
        }
        
        return;
      }

      // Check for critical duplicates that block progression
      const criticalDuplicates = [];
      if (this.duplicateChecks.account_number.isDuplicate) {
        criticalDuplicates.push(`${this.accountNoLabel} already exists`);
      }
      if (this.duplicateChecks.herd_no.isDuplicate) {
        criticalDuplicates.push('Herd Number already exists');
      }
      
      if (criticalDuplicates.length > 0) {
        this.$swal.fire({
          icon: 'error',
          title: 'Duplicate Information Detected',
          text: `Cannot proceed: ${criticalDuplicates.join(', ')}. Please check the existing farmer or use different values.`,
          confirmButtonText: 'OK'
        });
        return;
      }

      // Handle edit mode vs create mode
      if (this.isEditing) {
        // In edit mode, update the farmer immediately
        this.updateFarmer();
        // Prevent create-flow success modals from showing during edit flow
        return;
      } else {
        // In create mode, proceed to step 2 - don't create farmer yet
        this.step = 2;
      }
      
      // Auto-set farm location based on eircode if available
      if (this.eircodeCoordinates && !this.farmForm.location.length) {
        // Set default farm location to eircode coordinates
        this.farmForm.location = [this.eircodeCoordinates.lng, this.eircodeCoordinates.lat];
        this.farmForm.location_xy = [this.eircodeCoordinates.lng, this.eircodeCoordinates.lat];
        
        // Also emit an event to show the marker on the map
        setTimeout(() => {
          this.$emitter.emit("showLocationMarker", {
            coordinates: [this.eircodeCoordinates.lng, this.eircodeCoordinates.lat],
            type: "farm-location"
          });
        }, 500);
        
        this.$swal.fire({
          icon: 'success',
          title: 'Information Saved!',
          text: `Farmer information collected. We've set the default farm location based on your eircode.`,
          timer: 2500,
          showConfirmButton: false
        });
      } else {
        this.$swal.fire({
          icon: 'success',
          title: 'Information Saved!',
          text: `Farmer information collected. Now let's add their first farm.`,
          timer: 2000,
          showConfirmButton: false
        });
      }
    },

    addParcels() {
      if (this.createdFarmer && this.createdFarmer.hashid) {
        // Navigate to the farmer's farms view where they can add parcels
        this.$inertia.visit(route('app.farmer.view', this.createdFarmer.hashid) + '?tab=farms');
      }
    },

    linkLpisParcels() {
      if (this.createdFarmer && this.createdFarmer.hashid) {
        // The farms tab opens the map in LPIS linking mode
        this.$inertia.visit(route('app.farmer.view', this.createdFarmer.hashid) + '?tab=farms&action=link-lpis');
      }
    },

    importParcels() {
      if (this.createdFarmer && this.createdFarmer.hashid) {
        // Navigate to the farmer's farms view and trigger GeoJSON import
        // The farms component will handle the import process
        this.$inertia.visit(route('app.farmer.view', this.createdFarmer.hashid) + '?tab=farms&action=import');
      }
    },

    addFarmPoint(action) {
      // Same implementation as FarmWidget - enable map interaction for location setting
      this.$root.showMapOnMobile = true;
      this.showFormOverlay = true;
      
      // Show appropriate toast message
      let message = this.getToastMessage(action);
      this.toast.info(message);

      // Determine which coordinates to zoom to
      let zoomCoordinates = null;
      if (action === "farm-location") {
        if (this.farmForm.location.length) {
          // If farm location is already set, zoom to it
          zoomCoordinates = this.farmForm.location;
        } else if (this.eircodeCoordinates) {
          // If no farm location but we have eircode, zoom to eircode
          zoomCoordinates = [this.eircodeCoordinates.lng, this.eircodeCoordinates.lat];
        }
      }

      if (zoomCoordinates) {
        // Emit zoom event first
        this.$emitter.emit("zoomToLocation", {
          coordinates: zoomCoordinates,
          zoom: 16 // Good zoom level for farm location setting
        });
        
        // Show marker at the current location
        setTimeout(() => {
          this.$emitter.emit("showLocationMarker", {
            coordinates: zoomCoordinates,
            type: "farm-location"
          });
        }, 300);
        
        // Small delay to allow map to zoom before enabling point selection
        setTimeout(() => {
          this.$emitter.emit("addFarmPoint", {
            action,
            type: "create-farm",
          });
        }, 500);
      } else {
        // Emit event to enable map point selection
        this.$emitter.emit("addFarmPoint", {
          action,
          type: "create-farm",
        });
      }
    },

    getToastMessage(action) {
      if (action == "farm-location") {
        if (this.farmForm.location.length) {
          return "The map will center on your current farm location. Click to adjust the position if needed.";
        } else if (this.eircodeCoordinates) {
          return "The map will center on your eircode location. Click to confirm or adjust the farm position.";
        } else {
          return "Click on the map to set the farm location.";
        }
      } else {
        return "Draw the boundary for the field.";
      }
    },

    async completeFarmSetup() {
      if (!this.farmForm.location.length) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Location Required',
          text: 'Please set a location for the farm before continuing.',
          confirmButtonText: 'OK'
        });
        return;
      }

      // If farmer and farm have already been created, just proceed to step 3
      if (this.createdFarmer) {
        this.step = 3;
        return;
      }

      this.farmSetupProcessing = true;

      try {
        // First create the farmer
        const farmerResponse = await axios.post(route("app.farmer.store"), this.form.data(), {
          headers: {
            'Accept': 'application/json',
          }
        });
        this.createdFarmer = farmerResponse.data.farmer;

        // Then create the farm with location for this farmer
        await axios.post(route("app.farm.store"), {
          name: this.farmForm.name,
          farmLocation: {
            location: this.farmForm.location,
            location_xy: this.farmForm.location_xy
          },
          farmer: {
            farmer_id: this.createdFarmer.id
          },
          action: 'create-farm'
        });
        
        this.$swal.fire({
          icon: 'success',
          title: 'Farm Created!',
          text: `Farmer "${this.form.name}" and farm "${this.farmForm.name}" have been created successfully with location set.`,
          timer: 3000,
          showConfirmButton: false
        });
        
        this.step = 3;
      } catch (error) {
        console.error('Error in setup process:', error);
        
        // Handle validation errors for farmer creation
        if (error.response && error.response.data.errors) {
          // If it's farmer validation errors, go back to step 1
          this.form.errors = error.response.data.errors;
          this.step = 1;
          this.$swal.fire({
            icon: 'error',
            title: 'Farmer Information Error',
            text: 'Please check the farmer information and try again.',
            confirmButtonText: 'OK'
          });
        } else {
          this.$swal.fire({
            icon: 'error',
            title: 'Setup Error',
            text: 'There was an error during setup. Please try again.',
            confirmButtonText: 'OK'
          });
        }
      } finally {
        this.farmSetupProcessing = false;
      }
    },

    viewFarmer() {
      if (this.createdFarmer && this.createdFarmer.hashid) {
        this.$inertia.visit(route('app.farmer.view', this.createdFarmer.hashid));
      } else {
        // Fallback - redirect to farmers list
        this.$inertia.visit(route('app.farmer.index'));
      }
    },

    skipUsers() {
      // Skip user invitation and go directly to completion
      this.invitesSent = 0;
      this.step = 4;
    },

    async completeSetup() {
      // The farmer was created back in step 2, before these user details existed,
      // so the invitations have to be sent from here.
      if (this.invitesProcessing || !this.hasCompleteUser || !this.createdFarmer?.hashid) {
        return;
      }

      this.invitesProcessing = true;
      this.form.errors = {};

      try {
        // Every row is sent so that validation errors come back keyed by the
        // row the user filled in; blank rows are dropped server side.
        this.invitesSent = await inviteFarmerUsers(
          this.createdFarmer.hashid,
          this.form.users
        );

        this.$swal.fire({
          icon: 'success',
          title: 'Invitations Sent!',
          text: this.invitationCount(this.invitesSent) + ' sent by email.',
          timer: 3000,
          showConfirmButton: false
        });

        this.step = 4;
      } catch (error) {
        if (error.fieldErrors) {
          this.form.errors = error.fieldErrors;
        }

        this.$swal.fire({
          icon: 'error',
          title: 'Invitation Error',
          text: error.fieldErrors
            ? 'Please check the user details and try again.'
            : 'The invitations could not be sent. Please try again.',
          confirmButtonText: 'OK'
        });
      } finally {
        this.invitesProcessing = false;
      }
    },

    invitationCount(count) {
      return count + ' invitation' + (count === 1 ? '' : 's');
    },

    // Duplicate checking methods
    async checkDuplicate(field, value) {
      if (!value || !value.trim()) {
        this.duplicateChecks[field] = { isDuplicate: false, loading: false };
        return;
      }

      // Don't check if no organization is selected
      if (!this.currentOrganisation || !this.currentOrganisation.hashid) {
        console.log('No organisation selected for duplicate check', {
          currentOrganisation: this.currentOrganisation,
          userRole: this.$page.props.auth.user.role
        });
        return;
      }

      console.log('Checking duplicate for', { field, value, org: this.currentOrganisation.hashid });

      this.duplicateChecks[field].loading = true;
      
      try {
        const response = await axios.post('/api/farmer/check-duplicate', {
          field: field,
          value: value.trim(),
          organisation_id: this.currentOrganisation.hashid
        });

        this.duplicateChecks[field] = {
          isDuplicate: response.data.isDuplicate,
          loading: false,
          existingFarmer: response.data.existingFarmer || null
        };
        
        console.log('Duplicate check result:', response.data);
      } catch (error) {
        console.error('Error checking duplicate:', error);
        this.duplicateChecks[field] = { isDuplicate: false, loading: false };
      }
    },

    // Debounced duplicate checking methods
    onNameChange: _.debounce(function() {
      this.checkDuplicate('name', this.form.name);
    }, 500),

    onAccountNumberChange: _.debounce(function() {
      // Check duplicates for any organisation that has an account number field
      // (Currently all organisations can have account numbers, but validation rules may differ)
      if (this.form.account_number && this.currentOrganisation && this.currentOrganisation.hashid) {
        this.checkDuplicate('account_number', this.form.account_number);
      }
    }, 500),

    onHerdNoChange: _.debounce(function() {
      this.checkDuplicate('herd_no', this.form.herd_no);
    }, 500),

    updateFarmer() {
      // Update existing farmer; backend now redirects directly to index with flash
      let url = route("app.farmer.update", this.farmer.id);
      if (this.redirectTo) {
        // Append return param to inform backend desired redirect
        url += (url.includes('?') ? '&' : '?') + 'return=' + encodeURIComponent(this.redirectTo);
      }
      this.form.put(url, {
        onSuccess: () => {
          // Show toast instead of relying on flash bar
          if (this.toast) {
            this.toast.clear();
            this.toast.success('Farmer updated successfully');
          }
        },
        onError: (errors) => {
          console.error('Update failed:', errors);
          this.$swal.fire({
            icon: 'error',
            title: 'Update Failed',
            text: 'There was an error updating the farmer. Please check the form for errors.',
            confirmButtonText: 'OK'
          });
        }
      });
    },

    store() {
      // Keep the original method as fallback, but it won't be used in the new flow
      this.form.post(route("app.farmer.store"), {
        onSuccess: () => {
          this.form.reset();
        },
      });
    },
  },
};
</script>
